import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from 'src/app/common-components/confirmation-dialog/models/confirmation-dialog.config';
import { RestApiError } from 'src/app/common-components/errors/models/error-message';
import { UserPasswordResetRequestModel } from '../models/user-password-reset-request.model';
import { UserPasswordResetRequestDialogService } from '../services/user-password-reset-request-dialog.service';
import {
   submitUserPasswordResetRequest,
   submitUserPasswordResetRequestFailure,
   submitUserPasswordResetRequestSuccess,
   userPasswordResetRequest,
} from './user-password-reset-request.actions';

@Injectable()
export class UserPasswordResetRequestEffects {
   userPasswordResetRequest = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(userPasswordResetRequest),
            exhaustMap(() => {
               const afterClosed = this.userPasswordResetRequestDialogService.open().afterClosed();
               return afterClosed;
            })
         );
      },
      { dispatch: false }
   );

   submitUserPasswordResetRequest = createEffect(() => {
      return this.actions$.pipe(
         ofType(submitUserPasswordResetRequest),
         exhaustMap((action) => {
            const userPasswordResetRequest: UserPasswordResetRequestModel = action.userPasswordResetRequest;
            return this.sendPasswordResetRequest(userPasswordResetRequest).pipe(
               map((response: HttpResponse<UserPasswordResetRequestModel>) => {
                  this.userPasswordResetRequestDialogService.close();
                  return submitUserPasswordResetRequestSuccess({
                     userPasswordResetRequest: response.body!,
                  });
               }),
               catchError((error: RestApiError) => {
                  return of(submitUserPasswordResetRequestFailure(error));
               })
            );
         })
      );
   });

   userPasswordResetRequestSucess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(submitUserPasswordResetRequestSuccess),
            exhaustMap(({ userPasswordResetRequest }) => {
               const dialogRef = this.confirmationService.confirm({
                  data: {
                     dialogType: ConfirmationDialogType.Ok,
                     header: 'Password Reset Request Sent',
                     message:
                        'An email has been sent to ' +
                        userPasswordResetRequest.emailAddress +
                        ' with instructions on how to complete the password reset.',
                  },
                  size: DialogSize.Medium,
               });
               return dialogRef.afterClosed();
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private userPasswordResetRequestDialogService: UserPasswordResetRequestDialogService,
      private httpClient: HttpClient,
      private confirmationService: ConfirmationDialogService
   ) {}

   private sendPasswordResetRequest(userPasswordResetRequest: UserPasswordResetRequestModel) {
      return this.httpClient.post<UserPasswordResetRequestModel>(
         this.apiService.apiUrl + '/users/passwordresetrequest',
         userPasswordResetRequest,
         {
            observe: 'response',
         }
      );
   }
}
